import React from "react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="privacy-page">
      <header className="border-b sticky top-0 z-30" style={{ borderColor: "var(--border)", background: "rgba(10,20,24,0.85)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-[900px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-2xl tracking-tighter">
            MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD
          </Link>
          <Link to="/" className="overline hover:text-white">← home</Link>
        </div>
      </header>

      <article className="max-w-[900px] mx-auto px-6 md:px-10 py-16" style={{ color: "var(--text-2)" }}>
        <div className="overline mb-4">// legal</div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-2" style={{ color: "var(--text)" }}>Privacy Policy</h1>
        <p className="text-sm">Last updated: February 16, 2026 · BETA</p>

        <section className="mt-10 space-y-6 leading-relaxed">
          <p>This Privacy Policy explains how <span style={{ color: "var(--accent)" }}>My Nexus Card</span>, a sole proprietorship operated by <strong style={{ color: "var(--text)" }}>George Tabora</strong> (6550 Rock Spring Dr., Bethesda, MD 20817, USA), collects, uses, and shares information when you use the Service. Contact: {emailLink}.</p>

          <H2>1. Information we collect</H2>
          <p><strong style={{ color: "var(--text)" }}>From you directly:</strong></p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Account info — name, email, password hash (or Google OAuth profile).</li>
            <li>Profile info — title, company, phone, website, LinkedIn, bio, tagline, photo.</li>
            <li>Your Content — contacts you add or scan (names, emails, phones, companies, notes, tags, event assignments, interaction history), templates you write, campaigns you build, referrals you collect.</li>
            <li>Business-card images you upload or capture (processed via AI, then discarded — see §3).</li>
            <li>Payment info — handled directly by Stripe; we never see or store your card number.</li>
          </ul>
          <p><strong style={{ color: "var(--text)" }}>Collected automatically:</strong></p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Log data — IP address, browser/device, timestamps, pages visited, errors.</li>
            <li>Authentication cookies / JWT tokens to keep you logged in.</li>
            <li>Aggregate usage analytics (no third-party trackers like Facebook Pixel).</li>
          </ul>

          <H2>2. How we use information</H2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To provide and operate the Service (store contacts, send emails/SMS on your command, run AI scans).</li>
            <li>To process payments and prevent fraud.</li>
            <li>To send transactional notifications (account changes, billing, security alerts).</li>
            <li>To respond to support requests.</li>
            <li>To improve the Service via aggregate, de-identified analytics.</li>
            <li>To comply with law and enforce our Terms.</li>
          </ul>
          <p>We <strong style={{ color: "var(--text)" }}>do not</strong> sell your data or Your Content. We do not train third-party AI models on Your Content.</p>

          <H2>3. AI processing of card images</H2>
          <p>When you scan a business card, the image is sent to <strong style={{ color: "var(--text)" }}>Google LLC's Gemini API</strong> for text extraction. Google processes the image only to return structured contact data and (per Google's enterprise API policy) does not use it to train its models. The image is not stored on our servers; only the extracted text is saved (under your account, as a contact). AI message drafts work the same way: your prompt and a brief context are sent to Gemini and the response returned to you.</p>

          <H2>4. Third-party processors (sub-processors)</H2>
          <p>The Service depends on these vendors. Each has its own privacy policy:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong style={{ color: "var(--text)" }}>Stripe, Inc.</strong> — payments &amp; billing</li>
            <li><strong style={{ color: "var(--text)" }}>Resend, Inc.</strong> — email delivery (recipient email + content)</li>
            <li><strong style={{ color: "var(--text)" }}>Twilio Inc.</strong> — SMS delivery (recipient phone + content)</li>
            <li><strong style={{ color: "var(--text)" }}>Google LLC</strong> — Gemini AI; Google OAuth (if you choose)</li>
            <li><strong style={{ color: "var(--text)" }}>MongoDB, Inc.</strong> — database hosting</li>
            <li>Cloud infrastructure provider (Kubernetes-based hosting)</li>
          </ul>

          <H2>5. Sharing of information</H2>
          <p>We share information only:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>With the sub-processors listed above, strictly to operate the Service.</li>
            <li>If you publish a public digital business card, the profile fields you elect to show (name, title, company, phone, website, LinkedIn, bio, tagline) are publicly accessible at the URL you share.</li>
            <li>If you enable "Referral mode," visitors of your public card may submit a referral that creates a contact in your account.</li>
            <li>If required by law, valid legal process, or to protect our rights, safety, or property.</li>
            <li>In connection with a future business sale or transfer; you'll be notified of any change in data controller.</li>
          </ul>

          <H2>6. Data retention</H2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Account &amp; Your Content — retained while your account is active, plus 30 days after cancellation for recovery, then permanently deleted (except as required by law).</li>
            <li>Payment records — retained for 7 years for tax/accounting compliance.</li>
            <li>Log data — typically 90 days.</li>
            <li>Card images — not stored beyond the immediate AI extraction (seconds).</li>
          </ul>

          <H2>7. Your rights</H2>
          <p>Depending on where you live (US: CCPA/Maryland; EU/UK: GDPR; Canada: PIPEDA), you have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong style={{ color: "var(--text)" }}>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong style={{ color: "var(--text)" }}>Correction</strong> — fix inaccurate data (you can edit most of it directly in-app).</li>
            <li><strong style={{ color: "var(--text)" }}>Deletion</strong> — request erasure (subject to legal retention).</li>
            <li><strong style={{ color: "var(--text)" }}>Portability</strong> — receive your data in a portable format.</li>
            <li><strong style={{ color: "var(--text)" }}>Opt-out</strong> — of any non-essential communications.</li>
            <li><strong style={{ color: "var(--text)" }}>No-sale</strong> — we do not sell personal data; California residents have the right to confirm this.</li>
          </ul>
          <p>To exercise any right, email {emailLink}. We respond within 30 days. We may verify your identity before processing the request.</p>

          <H2>8. Contacts you upload (third-party data)</H2>
          <p>When you scan or import contact information about another person, you act as the data controller for that information. You represent that you have a lawful basis to process it (e.g., they handed you a card, consented to follow-up, or another legitimate interest). If a person whose data you hold contacts us directly, we will refer them to you and may delete their record if required by law.</p>

          <H2>9. Security</H2>
          <p>We use HTTPS in transit, encrypted databases at rest, hashed passwords (bcrypt), JWT tokens, and least-privilege access. No system is 100% secure; you use the Service at your own risk. Report vulnerabilities to {emailLink}.</p>

          <H2>10. Children</H2>
          <p>The Service is not directed to children under 16. We do not knowingly collect data from anyone under 16. If you believe a minor has registered, contact us and we'll delete the account.</p>

          <H2>11. International users</H2>
          <p>The Service is hosted in the United States. By using it, you consent to the transfer of your data to the U.S. We rely on Standard Contractual Clauses or equivalent mechanisms for EU/UK data transfers handled by our sub-processors.</p>

          <H2>12. Cookies &amp; tracking</H2>
          <p>We use essential cookies / localStorage for authentication only. We do not use advertising trackers, social-media pixels, or third-party analytics that profile you across the web.</p>

          <H2>13. Changes to this policy</H2>
          <p>We may update this Privacy Policy. Material changes will be announced via in-app notice or email at least 14 days before taking effect.</p>

          <H2>14. Contact</H2>
          <p>Privacy questions, requests, or complaints: George Tabora — {emailLink} — 6550 Rock Spring Dr., Bethesda, MD 20817, USA.</p>

          <div className="mt-12 pt-8 border-t flex flex-wrap justify-between items-center gap-4" style={{ borderColor: "var(--border)" }}>
            <Link to="/terms" className="overline hover:text-white">Terms &amp; Conditions →</Link>
            <span className="overline">© 2026 My Nexus Card · George Tabora · BETA</span>
          </div>
        </section>
      </article>
    </div>
  );
}

const emailLink = (
  <a href="mailto:georgeatabora@gmail.com" className="underline" style={{ color: "var(--accent)" }}>georgeatabora@gmail.com</a>
);

function H2({ children }) {
  return <h2 className="font-display font-bold text-2xl mt-10 mb-3 tracking-tight" style={{ color: "var(--text)" }}>{children}</h2>;
}
